package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.math.round

/**
 * Completed-event Lineups tab for non-Pro events (Figma 7893:88005).
 *
 * Pitch background lives on `AppPageLayout`; this composable lays out the
 * "THE MATCH ENDED" headline followed by a wrapping grid of every confirmed
 * participant rendered as an Arena-sized player card.
 *
 * Pro events use [LineupsFieldDisplay] in MatchView instead — they need the
 * full formation field, while non-Pro events just enumerate the roster.
 */
@Composable
fun NonProCompletedLineups(
    event: EventDetailDto,
    onViewProfile: (String) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val sportName = event.sportName ?: "football"
    val confirmed = event.participants.filter { it.status.uppercase() == "CONFIRMED" }

    Column(
        modifier = modifier.fillMaxWidth().fillMaxHeight().sGap(28),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        MatchEndedHeader()

        // Wrapping grid of all confirmed player cards rendered right under
        // the header (Figma 7893:88005). `align-content: flex-start` keeps
        // the cards hugging the headline so the first row sits directly
        // below the title — `center` was previously pushing the whole
        // grid down into the middle of the remaining vertical space.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .styleModifier {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "${s(12)} ${s(12)}")
                    property("justify-content", "center")
                    property("align-content", "flex-start")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                }
        ) {
            confirmed.forEach { p ->
                val displayName = ((p.fullName?.split(" ")?.lastOrNull() ?: p.username)).uppercase()
                // Outer slot keeps the visual footprint of the card; the
                // click handler is NOT on it. Instead, a dedicated inner
                // Box (positioned absolutely below) handles taps with a
                // hit rectangle that hugs the FIFA card.png silhouette —
                // generous left/right insets and a heavier bottom inset
                // skip the decorative transparent strip that lives below
                // the player figure in the template artwork.
                Box(
                    modifier = Modifier
                        .sWidth(210)
                        .position(Position.Relative)
                        .styleModifier { property("aspect-ratio", "94 / 147") }
                ) {
                    if (!p.cardAssetsBasePath.isNullOrBlank()) {
                        PlayerCard(
                            playerName = displayName,
                            rating = p.overallRating.toInt(),
                            position = p.position ?: "",
                            sport = sportName,
                            size = PlayerCardSize.Arena,
                            avatarUrl = p.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            cardAssetsBasePath = p.cardAssetsBasePath,
                            cardTextColor = p.cardTextColor,
                            fillContainer = true,
                        )
                    } else {
                        PlayerCard(
                            playerName = displayName,
                            sport = sportName,
                            size = PlayerCardSize.Arena,
                            avatarUrl = p.avatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                            fillContainer = true,
                        )
                    }
                    // Tight click target. Bumped insets after the previous
                    // 8% / 22% still let taps land on the FIFA card.png's
                    // decorative bezel: sides 16% pull the click rect well
                    // inside the figure's silhouette, and the 30% bottom
                    // cap skips the entire empty pedestal underneath.
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .cursor(Cursor.Pointer)
                            .onClick { onViewProfile(p.userId) }
                            .styleModifier {
                                property("top", "5%")
                                property("left", "16%")
                                property("right", "16%")
                                property("bottom", "30%")
                                property("z-index", "2")
                            }
                    )
                    // Star + 1-decimal rating, overlaid on the card's
                    // top-right corner (Figma 7906:378910). Always rendered
                    // — including `0.0` for players that haven't been rated
                    // yet — so the visual position stays consistent across
                    // every card in the grid.
                    val ratingText = (round(p.overallRating * 10) / 10.0).toString()
                    Row(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sGap(3)
                            .styleModifier {
                                // 30% right inset — the rating block hugs
                                // the upper-centre of the silhouette so
                                // its right edge stays well clear of the
                                // FIFA template's decorative bezel.
                                property("top", "8%")
                                property("right", "30%")
                                property("z-index", "5")
                                // Lets clicks pass through to the click
                                // box sitting one layer below — the
                                // rating itself is informational, not a
                                // separate click target.
                                property("pointer-events", "none")
                            },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        // Star icon recoloured to the Figma gold (7906:379049)
                        // by using the SVG as a mask and painting the fill
                        // via `background-color`. The shipped
                        // `ic_star_rating_16.svg` ships blue (#0061FF), which
                        // didn't match the rating design.
                        Box(
                            modifier = Modifier
                                .sSize(18)
                                .styleModifier {
                                    property("background-color", "#F2B73D")
                                    property("mask-image", "url('${AppIcons.StarRating16}')")
                                    property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                                    property("mask-size", "contain")
                                    property("-webkit-mask-size", "contain")
                                    property("mask-position", "center")
                                    property("-webkit-mask-position", "center")
                                    property("mask-repeat", "no-repeat")
                                    property("-webkit-mask-repeat", "no-repeat")
                                }
                        )
                        SpanText(
                            ratingText,
                            modifier = Modifier
                                .sFontSize(16)
                                .sLineHeight(20)
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("letter-spacing", s(0.032))
                                    property("font-family", "Urbanist, sans-serif")
                                }
                        )
                    }
                }
            }
        }
    }
}
